import { useLingui } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import DOMPurify from "isomorphic-dompurify";
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../Icon.tsx";
import "./expandable-section.css";

const BUTTON_HEIGHT = 56;

enum State {
  none = "none",
  open = "open",
  closed = "closed",
}

type Props = {
  headerText: string;
  bodyText: string;
  trackingEvent?: () => void;
  showLessText?: string;
  readMoreText?: string;
  className?: string;
};

export const ExpandableSection = ({
  headerText,
  bodyText,
  trackingEvent,
  showLessText,
  readMoreText,
  className,
}: Props) => {
  const { i18n } = useLingui();
  const [expandedState, setExpandedState] = useState<State>(State.none);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [elementTopPosition, setElementTopPosition] = useState<number | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const sanitizedHTML = DOMPurify.sanitize(bodyText);
  const COLLAPSED_MAX_HEIGHT = 280;

  const readMoreLabel =
    readMoreText ??
    i18n.t({
      id: "itemPageLayout.expandableSection.readMore",
      message: "Show more",
      comment: "Generic text for collapsing an expandable text section",
    });

  const showLessLabel =
    showLessText ??
    i18n.t("itemPageLayout.expandableSection.showLess", {
      message: "Show less",
      comment: "Generic text for expanding an expandable text section",
    });

  const getScrollRemaining = useCallback(() => {
    const rect = areaRef.current?.getBoundingClientRect();
    return rect ? rect.bottom - document.documentElement.clientHeight + BUTTON_HEIGHT : 0;
  }, []);

  const handleScroll = useCallback(() => {
    setHasScrolledToEnd(getScrollRemaining() <= 0);
  }, [getScrollRemaining]);

  // Use ResizeObserver to detect when content size changes (e.g. after fonts/CSS load)
  // so we can correctly switch from State.none → State.closed when content exceeds max height.
  useEffect(() => {
    const element = areaRef.current;
    if (!element) return;

    const checkExpandable = () => {
      setExpandedState((prev) => {
        if (prev === State.none && element.scrollHeight > COLLAPSED_MAX_HEIGHT) {
          return State.closed;
        }
        return prev;
      });
    };

    const observer = new ResizeObserver(checkExpandable);
    observer.observe(element);

    // Initial check for cases where content is already at final size
    checkExpandable();

    window.addEventListener("scroll", handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const toggleExpandedState = () => {
    if (expandedState !== State.open) {
      const absoluteTop = areaRef.current?.offsetTop ?? 0;
      setElementTopPosition(absoluteTop);
      setScrollPosition(window.scrollY);
      setTimeout(() => {
        handleScroll();
      }, 0);
    } else {
      const currentTop = areaRef.current?.offsetTop ?? 0;
      const scrollAdjustment = currentTop - (elementTopPosition || 0);
      window.scrollTo(0, scrollPosition + scrollAdjustment);
    }

    if (trackingEvent && expandedState === State.closed) {
      trackingEvent();
    }

    setExpandedState((prev) => (prev === State.open ? State.closed : State.open));
  };

  return (
    <section className={className}>
      <h2 className="t3 mb-0">{headerText}</h2>

      <div
        data-testid="expandable-section"
        ref={areaRef}
        className={`overflow-hidden relative mt-16 ${expandedState === State.open ? "max-h-none" : "max-h-[280px]"}`}
      >
        <div
          className="whitespace-pre-wrap children:list-disc children:list-outside mb-8 import-decoration"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: html is sanitized
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />

        {expandedState === State.closed && (
          <span
            className="absolute bottom-0 right-0 left-0 h-[160px]"
            style={{ background: "linear-gradient(to top, var(--w-s-color-background), transparent)" }}
          ></span>
        )}
      </div>

      {expandedState !== State.none && (
        <div
          className={`py-6 ${
            expandedState === State.open && !hasScrolledToEnd
              ? "sticky bottom-0 s-bg w-full shadow-[rgba(0,0,0,0.24)_0px_-20px_20px_-19px]"
              : ""
          }`}
        >
          <Button
            variant="quiet"
            onClick={toggleExpandedState}
            data-testid="toggle-description"
            data-preview-dont-disable="true"
          >
            {expandedState === State.open ? (
              <span className="flex items-center">
                <Icon name="Minus" />
                <span className="px-8">{showLessLabel}</span>
              </span>
            ) : (
              <span className="flex items-center">
                <Icon name="Plus" />
                <span className="px-8">{readMoreLabel}</span>
              </span>
            )}
          </Button>
        </div>
      )}
    </section>
  );
};
